import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation } from '@tanstack/react-query';
import { signDpia, getErrorMessage, type DpiaStatus, type SignDpiaInput } from '@/lib/api';
import { Icon } from '@/components/ui';

/**
 * Écran de signature d'AIPD/DPIA (RGPD art. 35) — formulaire guidé (4 piliers
 * CNIL + signataire + attestation). Signer crée un dossier CHIFFRÉ côté serveur,
 * ce qui lève le gating de diffusion d'une enquête sensible.
 *
 * Pré-remplissage : si une AIPD existe déjà, on charge son contenu (révision) ;
 * sinon, on propose un canevas adapté au contexte (recherche, art. 9, anonymat)
 * que le responsable relit et complète — ce n'est PAS un blanc-seing.
 */

const FIELDS: { key: keyof Omit<SignDpiaInput, 'attestation'>; label: string; hint: string; rows: number }[] = [
  {
    key: 'processingDescription',
    label: '1. Description du traitement',
    hint: 'Finalités, nature des données (art. 9 : santé, vie familiale, vécu de l’enfant…), catégories de personnes, durée de conservation.',
    rows: 4,
  },
  {
    key: 'necessityProportionality',
    label: '2. Nécessité & proportionnalité',
    hint: 'Base légale, minimisation des données, finalité d’intérêt public/recherche, durée justifiée.',
    rows: 3,
  },
  {
    key: 'risksAndMeasures',
    label: '3. Risques pour les personnes & mesures',
    hint: 'Risques (ré-identification, divulgation) et mesures pour les réduire.',
    rows: 3,
  },
  {
    key: 'securityMeasures',
    label: '4. Mesures de sécurité',
    hint: 'Chiffrement, contrôle d’accès, hébergement, sauvegardes, traçabilité.',
    rows: 3,
  },
];

/** Canevas par défaut adapté à une enquête de recherche sensible anonyme. */
const DEFAULTS: Omit<SignDpiaInput, 'attestation' | 'signatoryName'> = {
  processingDescription:
    "Traitement à des fins de recherche scientifique sur la coparentalité et le conflit parental. Données de catégorie particulière (art. 9 RGPD : santé, vie familiale, vécu de l'enfant). Collecte par questionnaire en ligne anonyme. Durée de conservation conforme au paramétrage de l'enquête.",
  necessityProportionality:
    "Finalité d'intérêt public dans le domaine de la recherche (art. 9.2.j RGPD). Données strictement nécessaires aux objectifs de l'étude ; minimisation appliquée ; aucun identifiant direct du répondant n'est collecté.",
  risksAndMeasures:
    "Risque principal : ré-identification d'un répondant. Mesures : anonymat structurel (aucune IP ni user-agent conservés), seuil de k-anonymat sur les agrégats, chiffrement des réponses au repos.",
  securityMeasures:
    'Chiffrement applicatif AES-256-GCM des réponses, hébergement dans l’UE, accès créateur authentifié et tracé, durcissement du serveur, sauvegardes.',
  signatoryRole: 'Responsable de traitement',
};

export default function DpiaDialog({
  open,
  workspaceId,
  surveyId,
  existing,
  onClose,
  onSigned,
}: {
  open: boolean;
  workspaceId: string;
  surveyId: string;
  existing: DpiaStatus | null;
  onClose: () => void;
  onSigned: () => void;
}): JSX.Element | null {
  const [form, setForm] = useState<SignDpiaInput>(() => ({
    ...DEFAULTS,
    signatoryName: '',
    attestation: false,
  }));

  // (Ré)initialise le formulaire à l'ouverture — pré-rempli depuis l'AIPD
  // existante si présente, sinon depuis le canevas par défaut.
  useEffect(() => {
    if (!open) return;
    const c = existing?.content;
    setForm({
      processingDescription: c?.processingDescription ?? DEFAULTS.processingDescription,
      necessityProportionality: c?.necessityProportionality ?? DEFAULTS.necessityProportionality,
      risksAndMeasures: c?.risksAndMeasures ?? DEFAULTS.risksAndMeasures,
      securityMeasures: c?.securityMeasures ?? DEFAULTS.securityMeasures,
      signatoryName: c?.signatoryName ?? '',
      signatoryRole: c?.signatoryRole ?? DEFAULTS.signatoryRole,
      attestation: false,
    });
  }, [open, existing]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const mutation = useMutation({
    mutationFn: () => signDpia(workspaceId, surveyId, form),
    onSuccess: () => onSigned(),
  });

  if (!open) return null;

  const set = (k: keyof SignDpiaInput, v: string | boolean): void =>
    setForm((f) => ({ ...f, [k]: v }));

  const requiredFilled =
    FIELDS.every((f) => form[f.key].toString().trim().length > 0) &&
    form.signatoryName.trim().length > 0 &&
    form.signatoryRole.trim().length > 0;
  const canSubmit = requiredFilled && form.attestation && !mutation.isPending;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-text/40 backdrop-blur-sm p-4 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label="Signer l'analyse d'impact (AIPD)"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="card w-full max-w-2xl my-8 space-y-4" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <span className="shrink-0 w-9 h-9 rounded-full bg-primary-soft text-primary-strong inline-flex items-center justify-center">
            <Icon.Shield size={18} />
          </span>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold">Analyse d’impact (AIPD / DPIA)</h2>
            <p className="text-sm text-text-muted mt-1">
              Cette enquête traite des données sensibles (art. 9 RGPD). Une AIPD signée est
              obligatoire <strong>avant la diffusion</strong>. Relisez et complétez chaque section,
              puis signez : le contenu est chiffré et conservé comme preuve.
            </p>
            {existing?.signed && (
              <p className="text-xs text-text-muted mt-1">
                Dernière version signée : v{existing.version} le {existing.signedAtDay}
                {existing.signedByEmail ? ` par ${existing.signedByEmail}` : ''}.
              </p>
            )}
          </div>
        </div>

        {FIELDS.map((f) => (
          <div className="field" key={f.key}>
            <label className="field-label" htmlFor={`dpia-${f.key}`}>
              {f.label}
            </label>
            <p className="text-xs text-text-muted mb-1">{f.hint}</p>
            <textarea
              id={`dpia-${f.key}`}
              className="input-base"
              rows={f.rows}
              value={form[f.key] as string}
              onChange={(e) => set(f.key, e.target.value)}
            />
          </div>
        ))}

        <div className="grid sm:grid-cols-2 gap-3">
          <div className="field">
            <label className="field-label" htmlFor="dpia-signatory">
              Signataire (nom)
            </label>
            <input
              id="dpia-signatory"
              type="text"
              className="input-base"
              value={form.signatoryName}
              onChange={(e) => set('signatoryName', e.target.value)}
              placeholder="Prénom Nom"
              autoComplete="off"
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="dpia-role">
              Qualité du signataire
            </label>
            <input
              id="dpia-role"
              type="text"
              className="input-base"
              value={form.signatoryRole}
              onChange={(e) => set('signatoryRole', e.target.value)}
            />
          </div>
        </div>

        <p className="text-xs text-text-muted flex items-start gap-1.5">
          <Icon.Mail size={14} className="mt-0.5 shrink-0" />
          <span>
            La signature est enregistrée sous l’adresse e-mail de votre compte connecté
            (identité vérifiée) ; une confirmation vous y est envoyée comme preuve.
          </span>
        </p>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={form.attestation}
            onChange={(e) => set('attestation', e.target.checked)}
          />
          <span>
            J’atteste que cette analyse d’impact est complète, exacte et signée par le responsable
            de traitement, conformément à l’article 35 du RGPD.
          </span>
        </label>

        {mutation.isError && (
          <p role="alert" className="field-error">
            <Icon.AlertTriangle />
            <span>{getErrorMessage(mutation.error)}</span>
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="btn-ghost" onClick={onClose} disabled={mutation.isPending}>
            Annuler
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!canSubmit}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Signature…' : 'Signer l’AIPD'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
